import React, { useState, useEffect } from 'react';
import { 
  Users, 
  Plus, 
  Search, 
  Edit, 
  Mail,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Key,
  UserCheck,
  UserX,
  Trash2,
  Menu,
  Eye,
  EyeOff
} from 'lucide-react';
import DataTable from '../common/DataTable';
import ConfirmationModal from '../common/ConfirmationModal';
import { useToast, ToastContainer } from '../common/Toast';
import UserService from '../../services/userService';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';

// Menu options matching sidebar (path is used for routing and storage)
const MENU_OPTIONS = [
  { path: '/overview', label: 'Overview' },
  { path: '/sourcing', label: 'Sourcing' },
  { path: '/contracts', label: 'Contracts' },
  { path: '/projects', label: 'Projects' },
  { path: '/subcontracting', label: 'Subcontracting' },
  { path: '/assets', label: 'Assets' },
  { path: '/finance', label: 'Finance' },
  { path: '/ai', label: 'AI Intelligence' },
  { path: '/collaboration', label: 'Collaboration' },
  { path: '/integration', label: 'Integration' },
  { path: '/system', label: 'System' },
  { path: '/support', label: 'Support' }
];

const UserManagement = () => {
  const { success: showSuccess, toasts, removeToast } = useToast();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showAssignMenusModal, setShowAssignMenusModal] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10
  });

  // Form state
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    username: '',
    password: '',
    assignedMenus: [],
    status: 'active'
  });

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async (page = 1) => {
    setLoading(true);
    try {
      const params = {
        page,
        limit: pagination.itemsPerPage,
        search: searchTerm || undefined,
        sortBy: 'firstName',
        sortOrder: 'asc'
      };
      const response = await UserService.getUsers(params);
      const newUsers = response.data || [];
      const newPagination = response.pagination || {
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        itemsPerPage: 10
      };
      setUsers(newUsers);
      setPagination(newPagination);
    } catch (error) {
      console.error('Error loading users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (value) => {
    setSearchTerm(value);
    loadUsers(1);
  };

  const handleCreateUser = async () => {
    try {
      const payload = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        username: formData.username,
        password: formData.password || undefined,
        assignedMenus: formData.assignedMenus || [],
        status: formData.status
      };
      await UserService.createUser(payload);
      setShowCreateModal(false);
      setShowAssignMenusModal(false);
      setShowPassword(false);
      resetForm();
      showSuccess('User created successfully');
      loadUsers();
    } catch (error) {
      console.error('Error creating user:', error);
      const msg = error.response?.data?.message || error.message || 'Failed to create user';
      alert(msg);
    }
  };

  // Handle edit user
  const handleEditUser = async () => {
    try {
      const payload = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        username: formData.username,
        assignedMenus: formData.assignedMenus || [],
        isActive: formData.status === 'active'
      };
      await UserService.updateUser(selectedUser.id, payload);
      setShowEditModal(false);
      setShowAssignMenusModal(false);
      resetForm();
      setSelectedUser(null);
      loadUsers();
    } catch (error) {
      console.error('Error updating user:', error);
      alert(error.response?.data?.message || error.message);
    }
  };

  // Handle delete user
  const handleDeleteUser = async () => {
    try {
      await UserService.deleteUser(selectedUser.id);
      setShowDeleteModal(false);
      setSelectedUser(null);
      loadUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      alert(error.message);
    }
  };

  // Handle status change
  const handleStatusChange = async (userId, newStatus) => {
    try {
      await UserService.updateUserStatus(userId, newStatus);
      loadUsers();
    } catch (error) {
      console.error('Error updating user status:', error);
      alert(error.message);
    }
  };

  // Handle reset password
  const handleResetPassword = async (userId) => {
    try {
      await UserService.resetUserPassword(userId);
      alert('Password reset email sent successfully');
    } catch (error) {
      console.error('Error resetting password:', error);
      alert(error.message);
    }
  };

  const resetForm = () => {
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      username: '',
      password: '',
      assignedMenus: [],
      status: 'active'
    });
  };

  const openEditModal = (user) => {
    setSelectedUser(user);
    setFormData({
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      username: user.username,
      password: '',
      assignedMenus: user.assignedMenus || [],
      status: user.status
    });
    setShowEditModal(true);
  };

  const toggleMenuOption = (path) => {
    setFormData(prev => ({
      ...prev,
      assignedMenus: prev.assignedMenus.includes(path)
        ? prev.assignedMenus.filter(p => p !== path)
        : [...prev.assignedMenus, path]
    }));
  };

  // Open delete modal
  const openDeleteModal = (user) => {
    setSelectedUser(user);
    setShowDeleteModal(true);
  };

  // Get status badge
  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { color: '#10b981', icon: CheckCircle, text: 'Active' },
      inactive: { color: '#6b7280', icon: XCircle, text: 'Inactive' },
      suspended: { color: '#ef4444', icon: AlertCircle, text: 'Suspended' }
    };

    const config = statusConfig[status] || statusConfig.inactive;
    const Icon = config.icon;

    return (
      <span style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        padding: '4px 8px',
        borderRadius: '6px',
        fontSize: '12px',
        fontWeight: '500',
        backgroundColor: `${config.color}20`,
        color: config.color
      }}>
        <Icon size={12} />
        {config.text}
      </span>
    );
  };

  // Table columns - DataTable passes (value, row) to render; use row as the user object
  const columns = [
    {
      key: 'user',
      label: 'User',
      sortable: true,
      render: (_value, row) => {
        const user = row;
        if (!user) return null;
        
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              backgroundColor: 'var(--primary-color)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontSize: '16px',
              fontWeight: '600'
            }}>
              {user.firstName ? user.firstName.charAt(0) : 'U'}{user.lastName ? user.lastName.charAt(0) : 'U'}
            </div>
            <div>
              <div style={{ fontWeight: '600', color: '#1f2937' }}>
                {user.firstName || 'Unknown'} {user.lastName || 'User'}
              </div>
              <div style={{ fontSize: '12px', color: '#6b7280' }}>
                @{user.username || user.email || 'unknown'}
              </div>
            </div>
          </div>
        );
      }
    },
    {
      key: 'email',
      label: 'Email',
      sortable: true,
      render: (_value, row) => {
        const user = row;
        if (!user) return null;
        
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Mail size={14} style={{ color: '#6b7280' }} />
            <span style={{ fontSize: '14px' }}>{user.email || 'N/A'}</span>
          </div>
        );
      }
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (_value, row) => {
        const user = row;
        if (!user) return null;
        return getStatusBadge(user.status);
      }
    },
    {
      key: 'lastLogin',
      label: 'Last Login',
      sortable: true,
      render: (_value, row) => {
        const user = row;
        if (!user) return null;
        
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Clock size={14} style={{ color: '#6b7280' }} />
            <span style={{ fontSize: '14px' }}>
              {user.lastLogin ? new Date(user.lastLogin).toLocaleDateString() : 'Never'}
            </span>
          </div>
        );
      }
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (_value, row) => {
        const user = row;
        if (!user) return null;
        
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button
              onClick={() => openEditModal(user)}
              style={{
                padding: '6px',
                backgroundColor: '#f3f4f6',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                color: '#6b7280'
              }}
              title="Edit User"
            >
              <Edit size={16} />
            </button>
            
            {/*
            <button
              onClick={() => handleStatusChange(user.id, user.status === 'active' ? 'inactive' : 'active')}
              style={{
                padding: '6px',
                backgroundColor: user.status === 'active' ? '#fef3c7' : '#d1fae5',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                color: user.status === 'active' ? '#d97706' : '#059669'
              }}
              title={user.status === 'active' ? 'Deactivate User' : 'Activate User'}
            >
              {user.status === 'active' ? <UserX size={16} /> : <UserCheck size={16} />}
            </button>
            */}

            {/*
            <button
              onClick={() => handleResetPassword(user.id)}
              style={{
                padding: '6px',
                backgroundColor: '#f3f4f6',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                color: '#6b7280'
              }}
              title="Reset Password"
            >
              <Key size={16} />
            </button>
            */}

            {user.role !== 'Super Admin' && (
              <button
                onClick={() => openDeleteModal(user)}
                style={{
                  padding: '6px',
                  backgroundColor: '#fef2f2',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  color: '#dc2626'
                }}
                title="Delete User"
              >
                <Trash2 size={16} />
              </button>
            )}
          </div>
        );
      }
    }
  ];

  return (
    <>
      <PageBreadcrumb />
      <PageHeader 
        title="User Management"
        description="Manage user accounts, roles, and permissions across the system"
      />
      <div className="page-container">

      {/* Controls: Search + Add User only */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '24px',
        flexWrap: 'wrap',
        gap: '16px'
      }}>
        <div style={{ position: 'relative' }}>
          <Search size={20} style={{
            position: 'absolute',
            left: '12px',
            top: '50%',
            transform: 'translateY(-50%)',
            color: '#6b7280'
          }} />
          <input
            type="text"
            placeholder="Search users..."
            value={searchTerm}
            onChange={(e) => handleSearch(e.target.value)}
            style={{
              padding: '10px 12px 10px 40px',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              fontSize: '14px',
              width: '300px',
              fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
            }}
          />
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="btn-cam"
        >
          <Plus size={16} />
          Add User
        </button>
      </div>

      {/* Data Table - backend data only, no export/filter header buttons */}
      <DataTable
        data={users}
        columns={columns}
        loading={loading}
        pagination={pagination}
        pageSize={pagination.itemsPerPage || 10}
        searchable={false}
        sortable={true}
        exportable={false}
        showFilterButton={false}
        showActions={false}
        onSearchChange={(value) => handleSearch(value)}
        onPageChange={(page) => loadUsers(page)}
      />

      {/* Create User Modal */}
      {showCreateModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            width: '500px',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <h3 style={{
              fontSize: '20px',
              fontWeight: '600',
              color: '#1f2937',
              margin: '0 0 20px 0'
            }}>
              Create New User
            </h3>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                  First Name *
                </label>
                <input
                  type="text"
                  value={formData.firstName}
                  onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                  Last Name *
                </label>
                <input
                  type="text"
                  value={formData.lastName}
                  onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
                  }}
                />
              </div>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                Email *
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
                }}
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                Username *
              </label>
              <input
                type="text"
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
                }}
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                Password
              </label>
              <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Min 6 characters (user will login with email + this password)"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px 12px 10px 12px',
                    paddingRight: '44px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  title={showPassword ? 'Hide password' : 'Show password'}
                  style={{
                    position: 'absolute',
                    right: '8px',
                    padding: '4px',
                    border: 'none',
                    background: 'none',
                    cursor: 'pointer',
                    color: '#6b7280',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <button
                type="button"
                onClick={() => setShowAssignMenusModal(true)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '10px 16px',
                  backgroundColor: '#f3f4f6',
                  color: '#374151',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
                }}
              >
                <Menu size={18} />
                Assign Menus
                {formData.assignedMenus?.length > 0 && (
                  <span style={{ marginLeft: '4px', fontSize: '12px', color: '#6b7280' }}>
                    ({formData.assignedMenus.length} selected)
                  </span>
                )}
              </button>
            </div>

            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px'
            }}>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setShowAssignMenusModal(false);
                  setShowPassword(false);
                  resetForm();
                }}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#f3f4f6',
                  color: '#6b7280',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleCreateUser}
                className="btn-cam"
              >
                Create User
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Assign Menus Modal (on top of Create/Edit) */}
      {showAssignMenusModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1100
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            width: '400px',
            maxHeight: '80vh',
            overflow: 'auto'
          }}>
            <h3 style={{
              fontSize: '18px',
              fontWeight: '600',
              color: '#1f2937',
              margin: '0 0 16px 0'
            }}>
              Assign Menu Access
            </h3>
            <p style={{ fontSize: '13px', color: '#6b7280', margin: '0 0 16px 0' }}>
              Select the menu items this user can access. Unselected items will appear faded and unselectable when they log in.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {MENU_OPTIONS.map((opt) => (
                <label
                  key={opt.path}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '10px 12px',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    backgroundColor: formData.assignedMenus.includes(opt.path) ? '#eff6ff' : 'transparent',
                    border: '1px solid transparent'
                  }}
                >
                  <input
                    type="checkbox"
                    checked={formData.assignedMenus.includes(opt.path)}
                    onChange={() => toggleMenuOption(opt.path)}
                    style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                  />
                  <span style={{ fontSize: '14px', fontWeight: '500' }}>{opt.label}</span>
                </label>
              ))}
            </div>
            <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowAssignMenusModal(false)}
                style={{
                  padding: '10px 20px',
                  backgroundColor: 'var(--primary-color)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
                }}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            width: '500px',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <h3 style={{
              fontSize: '20px',
              fontWeight: '600',
              color: '#1f2937',
              margin: '0 0 20px 0'
            }}>
              Edit User
            </h3>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                  First Name *
                </label>
                <input
                  type="text"
                  value={formData.firstName}
                  onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                  Last Name *
                </label>
                <input
                  type="text"
                  value={formData.lastName}
                  onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
                  }}
                />
              </div>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                Email *
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
                }}
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                Username *
              </label>
              <input
                type="text"
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
                }}
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <button
                type="button"
                onClick={() => setShowAssignMenusModal(true)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '10px 16px',
                  backgroundColor: '#f3f4f6',
                  color: '#374151',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
                }}
              >
                <Menu size={18} />
                Assign Menus
                {formData.assignedMenus?.length > 0 && (
                  <span style={{ marginLeft: '4px', fontSize: '12px', color: '#6b7280' }}>
                    ({formData.assignedMenus.length} selected)
                  </span>
                )}
              </button>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                Status
              </label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
                }}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="suspended">Suspended</option>
              </select>
            </div>

            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px'
            }}>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setShowAssignMenusModal(false);
                  resetForm();
                  setSelectedUser(null);
                }}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#f3f4f6',
                  color: '#6b7280',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleEditUser}
                className="btn-cam"
              >
                Update User
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedUser(null);
        }}
        onConfirm={handleDeleteUser}
        title="Delete User"
        message={`Are you sure you want to delete ${selectedUser?.firstName} ${selectedUser?.lastName}? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />
      <ToastContainer toasts={toasts} removeToast={removeToast} />
      </div>
    </>
  );
};

export default UserManagement;
